import type {
  ADJUST_REPORTING_INTEGRATION_ID,
  APP_STORE_CONNECT_REPORTING_INTEGRATION_ID,
  APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID,
  APPLOVIN_ADS_REPORTING_INTEGRATION_ID,
  APPLOVIN_MAX_REPORTING_INTEGRATION_ID,
  APPSFLYER_REPORTING_INTEGRATION_ID,
  CUSTOM_REPORTING_INTEGRATION_ID,
  GOOGLE_ADS_REPORTING_INTEGRATION_ID,
  GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID,
  GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID,
  IRON_SOURCE_REPORTING_INTEGRATION_ID,
  LIFTOFF_REPORTING_INTEGRATION_ID,
  MERCHANTO_REPORTING_INTEGRATION_ID,
  META_ADS_REPORTING_INTEGRATION_ID,
  MICROSOFT_ADS_REPORTING_INTEGRATION_ID,
  MINTEGRAL_REPORTING_INTEGRATION_ID,
  MOLOCO_ADS_REPORTING_INTEGRATION_ID,
  PADDLE_REPORTING_INTEGRATION_ID,
  PAY_PAL_REPORTING_INTEGRATION_ID,
  PAY_PRO_REPORTING_INTEGRATION_ID,
  PINTEREST_REPORTING_INTEGRATION_ID,
  SNAPCHAT_ADS_REPORTING_INTEGRATION_ID,
  SOLIDGATE_REPORTING_INTEGRATION_ID,
  STRIPE_REPORTING_INTEGRATION_ID,
  TAPJOY_REPORTING_INTEGRATION_ID,
  TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID,
  UNITY_ADS_REPORTING_INTEGRATION_ID,
  VOLUUM_REPORTING_INTEGRATION_ID,
  X_ADS_REPORTING_INTEGRATION_ID,
  PROPELLER_ADS_REPORTING_INTEGRATION_ID,
  EVERFLOW_REPORTING_INTEGRATION_ID,
  SINGULAR_REPORTING_INTEGRATION_ID,
  BIGO_ADS_REPORTING_INTEGRATION_ID,
  IMPACT_REPORTING_INTEGRATION_ID,
  CHARGEBEE_REPORTING_INTEGRATION_ID,
  HILLTOP_ADS_REPORTING_INTEGRATION_ID,
  TRAFFIC_STARS_REPORTING_INTEGRATION_ID,
  TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID,
  SCALEO_REPORTING_INTEGRATION_ID,
  BRANCH_REPORTING_INTEGRATION_ID,
  REDDIT_REPORTING_INTEGRATION_ID,
  XSOLLA_REPORTING_INTEGRATION_ID,
  AFFISE_REPORTING_INTEGRATION_ID,
  DISPUTEUR_REPORTING_INTEGRATION_ID,
  REDTRACK_REPORTING_INTEGRATION_ID,
  FUNNELFOX_REPORTING_INTEGRATION_ID,
  PRIMER_REPORTING_INTEGRATION_ID,
  CHARGEBLAST_REPORTING_INTEGRATION_ID,
  BRAINTREE_REPORTING_INTEGRATION_ID,
  UNLIMIT_REPORTING_INTEGRATION_ID,
  FUNNELFOX_BILLING_REPORTING_INTEGRATION_ID,
} from './constants';
import type { DatasourceIds } from './useDatasources';

export enum DatasourceType {
  marketingSpend = 'marketingSpend',
  attribution = 'attribution',
  monetization = 'monetization',
  affiliateNetwork = 'affiliateNetwork',
  tax = 'tax',
}

export enum DatasourceStatus {
  inactive = 'inactive',
  active = 'active',
  error = 'error',
}

export interface GenericDatasource {
  id: Exclude<DatasourceIds, DatasourceIds.custom>;
  name: string;
  type: DatasourceType;
  iconUrl: string;
  status: DatasourceStatus;
  sourceIntegrationIds: string[];
}

export type CustomDatasource = Omit<GenericDatasource, 'type' | 'id'> & {
  id: DatasourceIds.custom;
};

export type Datasource = GenericDatasource | CustomDatasource;

export interface AccountInfo {
  id: string;
  name: string;
}

export type ActiveStatusRecord = Record<string, true>;

export interface WebhookConfiguration {
  webhookPath: string;
  appHash: string;
}

export type MetaAdsReportingIntegration = Integration<{
  expirationDate?: number;
  adAccounts: AccountInfo[];
  useAllAccounts: boolean;
  adAccountsActiveStatusRecord: ActiveStatusRecord;
  id: string;
  name: string;
}>;

export type SnapchatAdsReportingIntegration = Integration<{
  email: string;
}>;

export type TikTokForBusinessReportingIntegration = Integration<{
  userId: string;
  name: string;
  email: string;
  advertiserIds: string[];
}>;

export type GoogleAdsReportingIntegration = Integration<{
  email: string;
  useAllAccounts: boolean;
  accountList: AccountInfo[];
  accountListActiveStatusRecord: ActiveStatusRecord;
}>;

export interface AppleSearchAdsReportingIntegrationConfig {
  accountName: string;
  teamId: string;
  keyId: string;
  clientId: string;
}

export type AppleSearchAdsReportingIntegration = Integration<
  Omit<AppleSearchAdsReportingIntegrationConfig, 'keyId' | 'clientId'>
>;

export interface AppLovinAdsReportingIntegrationConfig {
  accountName: string;
  reportKey: string;
}

export type AppLovinAdsReportingIntegration = Integration<
  Omit<AppLovinAdsReportingIntegrationConfig, 'reportKey'>
>;

export interface GooglePlayConsoleReportingIntegrationConfig {
  name: string;
  cloudStorageURI: string;
  serviceCredentials: string;
}

export type GooglePlayConsoleReportingIntegration = Integration<
  Omit<GooglePlayConsoleReportingIntegrationConfig, 'serviceCredentials'>
>;

export interface AppStoreConnectReportingIntegrationConfig {
  name: string;
  keyId: string;
  issuerId: string;
  vendorId: string;
  p8Key: string;
}

export type AppStoreConnectReportingIntegration = Integration<
  Omit<
    AppStoreConnectReportingIntegrationConfig,
    'issuerId' | 'vendorId' | 'p8Key'
  >
>;

export type AdjustReportingIntegrationConfig = {
  accountId: string;
  apiToken: string;
};

export type AdjustReportingIntegration = Integration<
  Omit<AdjustReportingIntegrationConfig, 'apiToken'>
>;

export type StripeReportingIntegrationConfig = {
  accountId: string;
  secretKey: string;
  signingSecret: string;
} & Partial<WebhookConfiguration>;

export type StripeReportingIntegration = Integration<
  Omit<StripeReportingIntegrationConfig, 'secretKey' | 'signingSecret'>
>;

export type PayPalReportingIntegrationConfig = {
  webhookId: string;
  clientId: string;
  clientSecret: string;
} & Partial<WebhookConfiguration>;

export type PayPalReportingIntegration = Integration<
  Omit<PayPalReportingIntegrationConfig, 'clientSecret'>
>;

export interface MolocoAdsReportingIntegrationConfig {
  accountName: string;
  adAccounts: string;
  apiKey: string;
}

export type MolocoAdsReportingIntegration = Integration<
  Omit<MolocoAdsReportingIntegrationConfig, 'apiKey'>
>;

export interface UnityAdsReportingIntegrationConfig {
  accountName: string;
  organizationId: string;
  keyId: string;
  secretKey: string;
}

export type UnityAdsReportingIntegration = Integration<
  Omit<UnityAdsReportingIntegrationConfig, 'secretKey'>
>;

export interface IronSourceReportingIntegrationConfig {
  accountName: string;
  secretKey: string;
  refreshToken: string;
}

export type IronSourceReportingIntegration = Integration<
  Omit<IronSourceReportingIntegrationConfig, 'secretKey' | 'refreshToken'>
>;

export interface XAdsReportingIntegrationConfig {
  apiKey: string;
  apiKeySecret: string;
  accessToken: string;
  accessTokenSecret: string;
}

export type XAdsReportingIntegration = Integration<
  Omit<
    XAdsReportingIntegrationConfig,
    'apiKeySecret' | 'accessToken' | 'accessTokenSecret'
  >
>;

export interface LiftoffReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
  apiSecret: string;
}

export type LiftoffReportingIntegration = Integration<
  Omit<LiftoffReportingIntegrationConfig, 'apiKey' | 'apiSecret'>
>;

export type AppsflyerReportingIntegrationConfig = {
  accountName: string;
  token: string;
} & Partial<WebhookConfiguration>;

export type AppsflyerReportingIntegration = Integration<
  Omit<AppsflyerReportingIntegrationConfig, 'token'>
>;

export interface GoogleAnalyticsReportingIntegrationConfig {
  bucket: string;
  subscriptionId: string;
}

export type GoogleAnalyticsReportingIntegration =
  Integration<GoogleAnalyticsReportingIntegrationConfig>;

export interface MintegralReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
  accessKey: string;
}

export type MintegralReportingIntegration = Integration<
  Omit<MintegralReportingIntegrationConfig, 'apiKey' | 'accessKey'>
>;

export interface AppLovinMaxReportingIntegrationConfig {
  accountName: string;
  reportKey: string;
}

export type AppLovinMaxReportingIntegration = Integration<
  Omit<AppLovinMaxReportingIntegrationConfig, 'reportKey'>
>;

export interface TapJoyReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type TapJoyReportingIntegration = Integration<
  Omit<TapJoyReportingIntegrationConfig, 'apiKey'>
>;

export type PinterestReportingIntegration = Integration<{
  username: string;
}>;

export type MicrosoftAdsReportingIntegration = Integration<{
  userEmail: string;
}>;

export type SolidgateReportingIntegrationConfig = {
  accountName: string;
  publicKey: string;
  secretKey: string;
} & Partial<WebhookConfiguration>;

export type SolidgateReportingIntegration = Integration<
  Omit<SolidgateReportingIntegrationConfig, 'secretKey'>
>;

export type PaddleReportingIntegrationConfig = {
  accountName: string;
  apiKey: string;
} & Partial<WebhookConfiguration>;

export type PaddleReportingIntegration = Integration<
  Omit<PaddleReportingIntegrationConfig, 'apiKey'>
>;

export interface VoluumReportingIntegrationConfig {
  accountName: string;
  accessKeyId: string;
  accessKey: string;
}

export type VoluumReportingIntegration = Integration<
  Omit<VoluumReportingIntegrationConfig, 'accessKey'>
>;

export type PayProReportingIntegrationConfig = {
  accountName: string;
  vendorAccountId: string;
  apiSecretKey: string;
} & Partial<WebhookConfiguration>;

export type PayProReportingIntegration = Integration<
  Omit<PayProReportingIntegrationConfig, 'apiSecretKey'>
>;

export interface MerchantoReportingIntegrationConfig {
  accountName: string;
  username: string;
  password: string;
}

export type MerchantoReportingIntegration = Integration<
  Omit<MerchantoReportingIntegrationConfig, 'username' | 'password'>
>;

export interface CustomReportingIntegrationConfig {
  accountName: string;
  configurationKeys: string[];
  configuration: Record<string, string>;
}

export type CustomReportingIntegration = Integration<
  Omit<CustomReportingIntegrationConfig, 'configuration'>
>;

export interface PropellerAdsReportingIntegrationConfig {
  accountName: string;
  apiToken: string;
}

export type PropellerAdsReportingIntegration = Integration<
  Omit<PropellerAdsReportingIntegrationConfig, 'apiToken'>
>;

export interface EverflowReportingIntegrationConfig {
  accountName: string;
  apiToken: string;
}

export type EverflowReportingIntegration = Integration<
  Omit<EverflowReportingIntegrationConfig, 'apiToken'>
>;

export interface SingularReportingIntegrationConfig {
  accountName: string;
  apiToken: string;
  awsKeyId?: string;
  awsKeySecret?: string;
  withAWSCredentials: boolean;
}

export type SingularReportingIntegration = Integration<
  Omit<
    SingularReportingIntegrationConfig,
    'apiToken' | 'awsKeyId' | 'awsKeySecret'
  >
>;

export interface BigoAdsReportingIntegrationConfig {
  accountName: string;
  clientId: string;
  clientSecret: string;
}

export type BigoAdsReportingIntegration = Integration<
  Omit<BigoAdsReportingIntegrationConfig, 'clientId' | 'clientSecret'>
>;

export interface ImpactReportingIntegrationConfig {
  accountName: string;
  accountSID: string;
  authToken: string;
}

export type ImpactReportingIntegration = Integration<
  Omit<ImpactReportingIntegrationConfig, 'accountSID' | 'authToken'>
>;

export type ChargebeeReportingIntegrationConfig = {
  accountName: string;
  apiKey: string;
} & WebhookConfiguration;

export type ChargebeeReportingIntegration = Integration<
  Omit<ChargebeeReportingIntegrationConfig, 'apiKey'>
>;

export interface HilltopAdsReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type HilltopAdsReportingIntegration = Integration<
  Omit<HilltopAdsReportingIntegrationConfig, 'apiKey'>
>;

export interface TrafficStarsReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type TrafficStarsReportingIntegration = Integration<
  Omit<TrafficStarsReportingIntegrationConfig, 'apiKey'>
>;

export interface TrafficJunkyReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type TrafficJunkyReportingIntegration = Integration<
  Omit<TrafficJunkyReportingIntegrationConfig, 'apiKey'>
>;

export interface ScaleoReportingIntegrationConfig {
  accountName: string;
  apiURL: string;
  apiToken: string;
}

export type ScaleoReportingIntegration = Integration<
  Omit<ScaleoReportingIntegrationConfig, 'apiToken'>
>;

export type BranchReportingIntegrationConfig = {
  accountName: string;
  appId: string;
  appToken: string;
} & Partial<WebhookConfiguration>;

export type BranchReportingIntegration = Integration<
  Omit<BranchReportingIntegrationConfig, 'appToken'>
>;

export type XsollaReportingIntegrationConfig = {
  accountName: string;
  merchantId: string;
  apiKey: string;
  secretKey: string;
} & WebhookConfiguration;

export type XsollaReportingIntegration = Integration<
  Omit<XsollaReportingIntegrationConfig, 'apiKey' | 'secretKey'>
>;

export interface RedditReportingIntegrationConfig {
  username: string;
}

export type RedditReportingIntegration =
  Integration<RedditReportingIntegrationConfig>;

export interface OAuth2Configuration {
  clientId: string;
  code: string;
  redirectURI: string;
}

export interface AffiseReportingIntegrationConfig {
  accountName: string;
  apiToken: string;
}

export type AffiseReportingIntegration = Integration<
  Omit<AffiseReportingIntegrationConfig, 'apiToken'>
>;

export interface DisputeurReportingIntegrationConfig {
  accountName: string;
  login: string;
  password: string;
}

export type DisputeurReportingIntegration = Integration<
  Omit<DisputeurReportingIntegrationConfig, 'password'>
>;

export interface RedTrackReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type RedTrackReportingIntegration = Integration<
  Omit<RedTrackReportingIntegrationConfig, 'apiKey'>
>;

export interface AnrokReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type AnrokReportingIntegration = Integration<
  Omit<AnrokReportingIntegrationConfig, 'apiKey'>
>;

export type FunnelFoxReportingIntegrationConfig = {
  accountName: string;
  apiKey: string;
} & Partial<WebhookConfiguration>;

export type FunnelFoxReportingIntegration = Integration<
  Omit<FunnelFoxReportingIntegrationConfig, 'apiKey'>
>;

export type PrimerReportingIntegrationConfig = {
  accountName: string;
  apiToken: string;
} & WebhookConfiguration;

export type PrimerReportingIntegration = Integration<
  Omit<PrimerReportingIntegrationConfig, 'apiToken'>
>;

export interface ChargeblastReportingIntegrationConfig {
  accountName: string;
  apiKey: string;
}

export type ChargeblastReportingIntegration = Integration<
  Omit<ChargeblastReportingIntegrationConfig, 'apiKey'>
>;

export type BraintreeReportingIntegrationConfig = {
  accountName: string;
  publicKey: string;
  merchantId: string;
  privateKey: string;
} & WebhookConfiguration;

export type BraintreeReportingIntegration = Integration<
  Omit<BraintreeReportingIntegrationConfig, 'privateKey'>
>;

export interface UnlimitReportingIntegrationConfig {
  accountName: string;
  terminalCode: string;
  password: string;
}

export type UnlimitReportingIntegration = Integration<
  Omit<UnlimitReportingIntegrationConfig, 'terminalCode' | 'password'>
>;

export type FunnelFoxBillingReportingIntegrationConfig = {
  accountName: string;
  organizationId: string;
  secretKey: string;
} & WebhookConfiguration;

export type FunnelFoxBillingReportingIntegration = Integration<
  Omit<FunnelFoxBillingReportingIntegrationConfig, 'secretKey'>
>;

export enum IntegrationStatus {
  connected = 'connected',
  pending = 'pending',
  failed = 'failed',
}

export interface Integration<T = unknown> {
  id: string;
  sourceIntegrationId:
    | typeof APP_STORE_CONNECT_REPORTING_INTEGRATION_ID
    | typeof APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID
    | typeof APPLOVIN_ADS_REPORTING_INTEGRATION_ID
    | typeof APPLOVIN_MAX_REPORTING_INTEGRATION_ID
    | typeof APPSFLYER_REPORTING_INTEGRATION_ID
    | typeof BIGO_ADS_REPORTING_INTEGRATION_ID
    | typeof CHARGEBEE_REPORTING_INTEGRATION_ID
    | typeof GOOGLE_ADS_REPORTING_INTEGRATION_ID
    | typeof GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID
    | typeof GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID
    | typeof IRON_SOURCE_REPORTING_INTEGRATION_ID
    | typeof LIFTOFF_REPORTING_INTEGRATION_ID
    | typeof META_ADS_REPORTING_INTEGRATION_ID
    | typeof MINTEGRAL_REPORTING_INTEGRATION_ID
    | typeof MOLOCO_ADS_REPORTING_INTEGRATION_ID
    | typeof PAY_PAL_REPORTING_INTEGRATION_ID
    | typeof PINTEREST_REPORTING_INTEGRATION_ID
    | typeof SNAPCHAT_ADS_REPORTING_INTEGRATION_ID
    | typeof STRIPE_REPORTING_INTEGRATION_ID
    | typeof TAPJOY_REPORTING_INTEGRATION_ID
    | typeof TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID
    | typeof UNITY_ADS_REPORTING_INTEGRATION_ID
    | typeof X_ADS_REPORTING_INTEGRATION_ID
    | typeof MICROSOFT_ADS_REPORTING_INTEGRATION_ID
    | typeof SOLIDGATE_REPORTING_INTEGRATION_ID
    | typeof ADJUST_REPORTING_INTEGRATION_ID
    | typeof PADDLE_REPORTING_INTEGRATION_ID
    | typeof VOLUUM_REPORTING_INTEGRATION_ID
    | typeof PAY_PRO_REPORTING_INTEGRATION_ID
    | typeof MERCHANTO_REPORTING_INTEGRATION_ID
    | typeof CUSTOM_REPORTING_INTEGRATION_ID
    | typeof PROPELLER_ADS_REPORTING_INTEGRATION_ID
    | typeof EVERFLOW_REPORTING_INTEGRATION_ID
    | typeof SINGULAR_REPORTING_INTEGRATION_ID
    | typeof IMPACT_REPORTING_INTEGRATION_ID
    | typeof HILLTOP_ADS_REPORTING_INTEGRATION_ID
    | typeof TRAFFIC_STARS_REPORTING_INTEGRATION_ID
    | typeof TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID
    | typeof SCALEO_REPORTING_INTEGRATION_ID
    | typeof BRANCH_REPORTING_INTEGRATION_ID
    | typeof REDDIT_REPORTING_INTEGRATION_ID
    | typeof XSOLLA_REPORTING_INTEGRATION_ID
    | typeof AFFISE_REPORTING_INTEGRATION_ID
    | typeof DISPUTEUR_REPORTING_INTEGRATION_ID
    | typeof REDTRACK_REPORTING_INTEGRATION_ID
    | typeof FUNNELFOX_REPORTING_INTEGRATION_ID
    | typeof PRIMER_REPORTING_INTEGRATION_ID
    | typeof CHARGEBLAST_REPORTING_INTEGRATION_ID
    | typeof BRAINTREE_REPORTING_INTEGRATION_ID
    | typeof UNLIMIT_REPORTING_INTEGRATION_ID
    | typeof FUNNELFOX_BILLING_REPORTING_INTEGRATION_ID;
  integrationConfig: T;
  updatedAt: string;
  status: IntegrationStatus;
  lastSuccessfulSync?: string;
  isConfigured: boolean;
}
